<template>
  <DashboardWidgetCard
    title="Activities"
    subtitle="Outreach logged in the selected period"
    icon="mdi:lightning-bolt-outline"
    accent="#4a3aff"
    :link-to="{ name: ACTIVITY_REPORTS_ROUTE }"
    link-label="Full report"
    :is-loading="isLoading"
  >
    <div class="activities-summary">
      <div class="activities-total">
        <span class="activities-total-value">{{ formatNumber(total) }}</span>
        <span class="activities-total-label">total activities</span>
      </div>

      <div
        class="activities-distribution"
        role="img"
        :aria-label="`Activity split across ${channels.length} channels`"
      >
        <template v-if="total > 0">
          <span
            v-for="channel in channels"
            v-show="channel.count > 0"
            :key="channel.key"
            class="distribution-segment"
            :style="{ width: `${channel.share}%`, background: channel.color }"
            :title="`${channel.label}: ${channel.count} (${channel.share.toFixed(0)}%)`"
          />
        </template>
        <span v-else class="distribution-empty" />
      </div>
    </div>

    <div class="activities-grid">
      <div v-for="channel in channels" :key="channel.key" class="activity-tile">
        <div class="activity-tile-top">
          <span
            class="activity-tile-icon"
            :style="{ color: channel.color, background: `${channel.color}1f` }"
          >
            <Icon :icon="channel.icon" width="18" height="18" />
          </span>
          <span class="activity-tile-share">{{ channel.share.toFixed(0) }}%</span>
        </div>
        <div class="activity-tile-value">{{ formatNumber(channel.count) }}</div>
        <div class="activity-tile-label">{{ channel.label }}</div>
        <div v-if="channel.meta" class="activity-tile-meta">
          <Icon icon="mdi:timer-outline" width="12" height="12" />
          {{ channel.meta }}
        </div>
      </div>
    </div>
  </DashboardWidgetCard>
</template>

<script setup>
import { computed } from "vue";
import { Icon } from "@iconify/vue";
import DashboardWidgetCard from "./DashboardWidgetCard.vue";
import { ACTIVITY_REPORTS_ROUTE } from "@/router/routes";
import { formatCallDuration } from "@/utils/durationFormatter";

defineOptions({
  name: "DashboardActivitiesWidget",
});

const props = defineProps({
  /** `dailyTaskTypeCounts` from dashboard stats: { "YYYY-MM-DD": { email, call, ... } } */
  dailyCounts: { type: Object, default: () => ({}) },
  /** `dailyCallDurations` from dashboard stats: { "YYYY-MM-DD": seconds } */
  dailyCallDurations: { type: Object, default: () => ({}) },
  isLoading: { type: Boolean, default: false },
});

const CHANNELS = [
  { key: "email", label: "Emails", icon: "mdi:email-outline", color: "#962dff" },
  { key: "call", label: "Calls", icon: "mdi:phone-outline", color: "#4a3aff" },
  { key: "linkedIn", label: "LinkedIn", icon: "mdi:linkedin", color: "#0a66c2" },
  { key: "whatsApp", label: "WhatsApp", icon: "mdi:whatsapp", color: "#1fa855" },
  { key: "sms", label: "SMS", icon: "mdi:message-text-outline", color: "#34b7f1" },
  { key: "other", label: "Others", icon: "mdi:dots-horizontal-circle-outline", color: "#2140a9" },
];

const totalsByChannel = computed(() => {
  const totals = Object.fromEntries(CHANNELS.map((c) => [c.key, 0]));
  Object.values(props.dailyCounts || {}).forEach((day) => {
    CHANNELS.forEach((c) => {
      totals[c.key] += Number(day?.[c.key]) || 0;
    });
  });
  return totals;
});

const totalCallSeconds = computed(() =>
  Object.values(props.dailyCallDurations || {}).reduce((sum, s) => sum + (Number(s) || 0), 0)
);

// "Meeting Booked" is intentionally excluded here (it lives in the Meetings widget),
// matching how the old chart's daily total was calculated.
const total = computed(() =>
  Object.values(totalsByChannel.value).reduce((sum, value) => sum + value, 0)
);

const channels = computed(() =>
  CHANNELS.map((c) => {
    const count = totalsByChannel.value[c.key];
    return {
      ...c,
      count,
      share: total.value > 0 ? (count / total.value) * 100 : 0,
      meta:
        c.key === "call" && totalCallSeconds.value > 0
          ? `${formatCallDuration(totalCallSeconds.value)} talk time`
          : "",
    };
  })
);

const formatNumber = (value) => new Intl.NumberFormat("en-US").format(value || 0);
</script>

<style scoped>
.activities-summary {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 16px;
}

.activities-total {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-shrink: 0;
}

.activities-total-value {
  font-size: 1.9rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

.activities-total-label {
  font-size: 0.85rem;
  color: #6b7280;
}

.activities-distribution {
  flex: 1;
  display: flex;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: #f3f4f6;
  gap: 2px;
}

.distribution-segment {
  height: 100%;
  transition: width 0.3s ease;
}

.distribution-empty {
  width: 100%;
}

.activities-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}

.activity-tile {
  padding: 14px;
  border: 1px solid #f0f0f5;
  border-radius: 12px;
  background: #fafafc;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.activity-tile:hover {
  border-color: #e0e0ef;
  box-shadow: 0 2px 8px rgba(17, 24, 39, 0.05);
}

.activity-tile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.activity-tile-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
}

.activity-tile-share {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
}

.activity-tile-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.1;
}

.activity-tile-label {
  margin-top: 2px;
  font-size: 0.82rem;
  font-weight: 500;
  color: #4b5563;
}

.activity-tile-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 0.72rem;
  color: #6b7280;
}

@media (max-width: 1280px) {
  .activities-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .activities-summary {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .activities-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
